import React, { useEffect, useState, useCallback } from 'react';
import { Wifi, Clock, Thermometer, AlertTriangle, Activity, Settings, AlertCircle } from 'lucide-react';
import { Device, supabase } from '../lib/supabase';
import EditableConfigField from './EditableConfigField';
import { getParameterConfig } from '../config/editableParameters';

/**
 * DeviceConfigDisplay Component
 * 
 * STABLE VERSION - August 2025
 * 
 * This component displays device configuration parameters fetched from the device_config table.
 * It is used by both regular users (via DevicesPageUserFiltered) and super admins (via DevicesPage).
 * 
 * IMPORTANT ARCHITECTURAL DECISIONS:
 * 1. Always fetches configuration from device_config table (source of truth)
 * 2. Ignores potentially stale/incorrect values from device_settings table
 * 3. Supports F0XX parameter fallback for devices with incomplete extraction
 * 
 * KEY F0XX PARAMETER MAPPINGS (AOVX Protocol):
 * - F005: NTP Port (hex encoded, e.g., 0000007b = 123)
 * - F006: Timezone offset (hex encoded)
 * - F010: Geofence enable (00=disabled, 01=enabled)
 * - F019: G-sensor enable (00=disabled, 01=enabled)
 * - F01A: G-sensor sensitivity threshold (hex, e.g., 64 = 100)
 * - F01B: G-sensor range (00=±2G, 01=±4G, 02=±8G, 03=±16G)
 * - F01C: G-sensor event count (hex, e.g., 0003 = 3)
 * - F01D: G-sensor time window (hex, e.g., 0000000a = 10 seconds)
 * - F01E: Movement report interval (hex, e.g., 00000258 = 600 seconds = 10 minutes)
 * - F020: Light sensor enable (00=disabled, 01=enabled)
 * 
 * DATABASE STRUCTURE:
 * - device_config.raw_config: Main configuration JSON (extracted parameters + f0_params)
 * - device_settings: User preferences and nicknames (DO NOT use for config values)
 * 
 * @param device - Device object passed from parent (may contain stale config data)
 * @param category - Configuration category to display (all, network, tracking, etc.)
 */
interface DeviceConfigDisplayProps {
  device: Device;
  category: string;
}

const DeviceConfigDisplay: React.FC<DeviceConfigDisplayProps> = ({ device, category }) => {
  const isDev = process.env.NODE_ENV !== 'production';
  const [deviceConfig, setDeviceConfig] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<any>({});
  const [changeStatuses, setChangeStatuses] = useState<{ [key: string]: 'pending' | 'applied' | 'failed' }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<{ iccid?: string; firmware_version?: string }>({});
  // We keep the Edit Config button visible, but actual editing is disabled from the Devices screen.
  const allowEditing = false;

  /**
   * Fetch configuration from device_config table
   * 
   * CRITICAL: This fetch is necessary because:
   * - Parent components may pass stale data from device_settings table
   * - device_config table contains the most up-to-date configuration
   * - Different app versions (warehouse vs multi-tenant) pass different data structures
   */
  // Fetch device configuration and pending changes
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const deviceId = device.device_id || device.id;
        
        // Fetch current config
        const { data: configData, error: configError } = await supabase
          .from('device_config')
          .select('raw_config, created_at, last_updated')
          .eq('device_id', deviceId)
          .order('last_updated', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (configError) {
          console.error('Error fetching device config:', configError);
        } else if (configData) {
          setDeviceConfig(configData.raw_config);
          
          try {
            const raw: any =
              typeof configData.raw_config === 'string'
                ? JSON.parse(configData.raw_config)
                : configData.raw_config;
            if (isDev) {
              console.log('[ConfigDisplay] Loaded device_config.raw_config thresholds', {
                deviceId,
                temp_threshold_low: raw?.temp_threshold_low,
                temperature_min: raw?.temperature_min,
                temp_min: raw?.temp_min,
                temp_threshold_high: raw?.temp_threshold_high,
                temperature_max: raw?.temperature_max,
                temp_max: raw?.temp_max,
              });
            }
          } catch (e) {
            if (isDev) {
              console.warn('[ConfigDisplay] Failed to parse raw_config for logging', {
                deviceId,
                error: (e as Error)?.message,
              });
            }
          }
        }
        
        // Fetch pending changes
        const { data: pendingData, error: pendingError } = await supabase
          .from('pending_config_changes')
          .select('*')
          .eq('device_id', deviceId)
          .eq('status', 'pending')
          .order('requested_at', { ascending: false })
          .limit(1);
        
        if (!pendingError && pendingData && pendingData.length > 0) {
          const pending = pendingData[0];
          setPendingChanges(pending.config_changes);
          
          // Set status for each pending parameter
          const statuses: { [key: string]: 'pending' } = {};
          Object.keys(pending.config_changes).forEach(key => {
            statuses[key] = 'pending';
          });
          setChangeStatuses(statuses);

          if (isDev) {
            console.log('[ConfigDisplay] Loaded pending_config_changes for device', {
              deviceId,
              config_changes: pending.config_changes,
              statuses,
            });
          }
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (device.device_id || device.id) {
      fetchConfig();
    } else {
      setLoading(false);
    }
  }, [device.device_id, device.id]);

  // Set up real-time subscription for config changes
  useEffect(() => {
    const deviceId = device.device_id || device.id;
    if (!deviceId) return;

    const subscription = supabase
      .channel(`config-changes-${deviceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'pending_config_changes',
          filter: `device_id=eq.${deviceId}`
        },
        (payload) => {
          if (payload.new) {
            const change = payload.new as any;
            if (change.status === 'pending') {
              setPendingChanges(change.config_changes);
              const statuses: { [key: string]: 'pending' } = {};
              Object.keys(change.config_changes).forEach(key => {
                statuses[key] = 'pending';
              });
              setChangeStatuses(statuses);
            } else if (change.status === 'applied') {
              // Clear pending changes and mark as applied
              setPendingChanges({});
              const statuses: { [key: string]: 'applied' } = {};
              Object.keys(change.config_changes).forEach(key => {
                statuses[key] = 'applied';
              });
              setChangeStatuses(statuses);
              
              // Clear applied status after 3 seconds
              setTimeout(() => {
                setChangeStatuses({});
              }, 3000);
              
              // Refresh config
              fetchConfigOnly();
            } else if (change.status === 'failed') {
              // Mark as failed
              const statuses: { [key: string]: 'failed' } = {};
              const errors: { [key: string]: string } = {};
              Object.keys(change.config_changes).forEach(key => {
                statuses[key] = 'failed';
                errors[key] = change.error_message || 'Failed to apply';
              });
              setChangeStatuses(statuses);
              setErrors(errors);
            }
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.device_id, device.id]);

  const fetchConfigOnly = async () => {
    const deviceId = device.device_id || device.id;
    const { data, error } = await supabase
      .from('device_config')
      .select('raw_config')
      .eq('device_id', deviceId)
      .single();
    
    if (!error && data) {
      setDeviceConfig(data.raw_config);
    }
  };

  // Fetch device info (ICCID and firmware) directly from gps_data
  useEffect(() => {
    const fetchDeviceInfo = async () => {
      try {
        const deviceId = device.device_id || device.id;
        
        // Fetch latest GPS data for this device to get ICCID and firmware
        const { data: gpsData, error: gpsError } = await supabase
          .from('gps_data')
          .select('raw_data, device_id')
          .or(`device_id.eq.${deviceId},device_id.eq.TEST_${deviceId}`)
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (!gpsError && gpsData && gpsData.length > 0) {
          const latestGps = gpsData[0];
          let rawData = latestGps.raw_data;
          
          // Parse raw_data if it's a string
          if (typeof rawData === 'string') {
            try {
              rawData = JSON.parse(rawData);
            } catch (e) {
              console.error('Error parsing raw_data:', e);
            }
          }
          
          // Extract ICCID and firmware from raw_data
          const iccid = rawData?.iccid;
          const firmware = rawData?.firmware;
          
          setDeviceInfo({
            iccid: iccid || undefined,
            firmware_version: firmware || undefined
          });
          
          console.log('🔍 DeviceConfigDisplay - Fetched device info:', {
            device_id: deviceId,
            iccid,
            firmware,
            raw_data: rawData
          });
        }
      } catch (err) {
        console.error('Error fetching device info:', err);
      }
    };
    
    fetchDeviceInfo();
  }, [device.device_id, device.id]);

  /**
   * Merge fetched config with device data
   */
  const deviceWithConfig = {
    ...device,
    raw_config: deviceConfig || device.raw_config,
    // CRITICAL: Override these fields to prevent using wrong values from device_settings
    reporting_interval: undefined,
    report_interval: undefined,
  };

  /**
   * Get configuration value with multiple fallback strategies
   */
  const getConfigValue = (primaryKey: string, alternateKeys?: string[], f0xxKey?: string) => {
    let value = null;
    
    // STEP 1: Check raw_config (source of truth from device_config table)
    if (deviceWithConfig.raw_config) {
      value = deviceWithConfig.raw_config[primaryKey];
      if ((value === null || value === undefined) && alternateKeys) {
        for (const key of alternateKeys) {
          value = deviceWithConfig.raw_config[key];
          if (value !== null && value !== undefined) break;
        }
      }
      
      // STEP 2: Check F0XX parameters if no extracted value found
      if ((value === null || value === undefined) && f0xxKey && deviceWithConfig.raw_config.f0_params) {
        const f0Param = deviceWithConfig.raw_config.f0_params[f0xxKey];
        if (f0Param) {
          const hexValue = f0Param.parsed || f0Param.value;
          if (hexValue && typeof hexValue === 'string' && hexValue.match(/^[0-9a-fA-F]+$/)) {
            value = parseInt(hexValue, 16);
          } else {
            value = hexValue;
          }
        }
      }
    }
    
    // STEP 3: Use device properties as last resort (but skip known problematic fields)
    if ((value === null || value === undefined)) {
      const skipFields = ['reporting_interval', 'report_interval', 'movement_report_interval'];
      if (!skipFields.includes(primaryKey)) {
        value = deviceWithConfig[primaryKey as keyof Device];
        if ((value === null || value === undefined) && alternateKeys) {
          for (const key of alternateKeys) {
            if (!skipFields.includes(key)) {
              value = deviceWithConfig[key as keyof Device];
              if (value !== null && value !== undefined) break;
            }
          }
        }
      }
    }
    
    return value;
  };

  // Handle saving a configuration change
  const handleSaveParameter = useCallback(async (paramKey: string, value: any) => {
    const deviceId = device.device_id || device.id;
    const config = getParameterConfig(paramKey);
    if (!config) throw new Error('Parameter not editable');

    // Create config change object
    const configChanges: any = { [paramKey]: value };
    
    // If parameter has F0XX key, also include it
    if (config.f0xxKey) {
      configChanges[`f0_params.${config.f0xxKey}`] = value;
    }

    // Get current config for backup
    const currentValue = getConfigValue(paramKey, [], config.f0xxKey);
    const previousConfig = { [paramKey]: currentValue };

    // Insert pending change
    const { error } = await supabase
      .from('pending_config_changes')
      .insert({
        device_id: deviceId,
        config_changes: configChanges,
        previous_config: previousConfig,
        status: 'pending',
        requested_by: (await supabase.auth.getUser()).data.user?.id
      });

    if (error) {
      throw error;
    }

    // Update local state
    setPendingChanges({ ...pendingChanges, [paramKey]: value });
    setChangeStatuses({ ...changeStatuses, [paramKey]: 'pending' });
    
    // Call edge function to notify Railway (will be implemented)
    try {
      await supabase.functions.invoke('update-device-config', {
        body: {
          device_id: deviceId,
          config_changes: configChanges
        }
      });
    } catch (err) {
      console.error('Failed to notify Railway:', err);
      // Don't throw - the change is still saved in pending_config_changes
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device, pendingChanges, changeStatuses, deviceConfig]);

  // Helper function to display a value or dash
  const displayValue = (value: any, suffix?: string) => {
    if (value === null || value === undefined || value === '') return '—';
    return suffix ? `${value} ${suffix}` : value;
  };

  // Helper to display enabled/disabled status
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const displayStatus = (value: any) => {
    const isEnabled = value === true || value === 1 || value === '1' || value === 'true' || value === '01';
    return (
      <span className={`font-medium ${isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
        {isEnabled ? 'Enabled' : 'Disabled'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-gray-500">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Edit Mode Toggle */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center gap-2">
          {Object.keys(pendingChanges).length > 0 && (
            <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
              <AlertCircle className="w-3 h-3" />
              <span>{Object.keys(pendingChanges).length} pending change{Object.keys(pendingChanges).length > 1 ? 's' : ''}</span>
            </div>
          )}
        </div>
        <button
          onClick={allowEditing ? () => setIsEditMode(!isEditMode) : undefined}
          disabled={!allowEditing}
          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
            isEditMode
              ? 'bg-blue-600 text-white hover:bg-blue-700'
              : 'bg-gray-200 text-gray-700'
          } ${!allowEditing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-300'}`}
          title={allowEditing ? 'Edit device configuration' : 'Editing is disabled from this screen'}
        >
          Edit Config
        </button>
      </div>
      {/* Network Configuration - 5 parameters */}
      {(category === 'all' || category === 'network') && (
        <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Wifi className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Network Configuration</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Server Address</span>
              <span className="font-medium text-gray-900 truncate max-w-[180px]" title={String(getConfigValue('main_server', ['server_ip']))}>
                {displayValue(getConfigValue('main_server', ['server_ip']))}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Server Port</span>
              <span className="font-medium text-gray-900">
                {displayValue(getConfigValue('main_server_port', ['server_port', 'tcp_port']))}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">NTP Server</span>
              <span className="font-medium text-gray-900">
                {displayValue(getConfigValue('ntp_server', ['ntp_ip']))}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">NTP Port</span>
              <span className="font-medium text-gray-900">
                {displayValue(getConfigValue('ntp_port', ['param_f005'], 'F005'))}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Timezone</span>
              <span className="font-medium text-gray-900">
                {(() => {
                  const tz = getConfigValue('timezone', ['param_f006'], 'F006');
                  if (tz === null || tz === undefined) return 'UTC+0';
                  const tzNum = Number(tz);
                  return `UTC${tzNum >= 0 ? '+' : ''}${tzNum}`;
                })()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Intervals - 2 parameters */}
      {(category === 'all' || category === 'tracking') && (
        <div className={`border rounded-lg p-3 group ${
          Object.keys(changeStatuses).some(k => k.startsWith('sampling_') || k.startsWith('reporting_') || k.startsWith('movement_'))
            ? 'border-amber-300 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Tracking Intervals</span>
          </div>
          <div className="space-y-1.5">
            {/* Normal Report Interval (F02E) - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('reporting_interval')!}
              currentValue={getConfigValue('reporting_interval', ['report_interval', 'normal_interval'], 'F02E')}
              pendingValue={pendingChanges['reporting_interval']}
              status={changeStatuses['reporting_interval'] || 'normal'}
              error={errors['reporting_interval']}
              onSave={(value) => handleSaveParameter('reporting_interval', value)}
              disabled={!isEditMode}
            />
            
            {/* Movement Interval - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('movement_report_interval')!}
              currentValue={getConfigValue('movement_report_interval', ['report_interval_movement'], 'F01E')}
              pendingValue={pendingChanges['movement_report_interval']}
              status={changeStatuses['movement_report_interval'] || 'normal'}
              error={errors['movement_report_interval']}
              onSave={(value) => handleSaveParameter('movement_report_interval', value)}
              disabled={!isEditMode}
            />
          </div>
        </div>
      )}

      {/* Motion Detection (G-Sensor) - 6 parameters */}
      {(category === 'all' || category === 'motion') && (
        <div className={`border rounded-lg p-3 group ${
          Object.keys(changeStatuses).some(k => k.startsWith('gsensor'))
            ? 'border-amber-300 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Motion Detection</span>
          </div>
          <div className="space-y-1.5">
            {/* Motion Detection Enable - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('gsensor_enable')!}
              currentValue={getConfigValue('gsensor_enable', ['g_sensor_alarm_enable', 'g_sensor_enabled', 'motion_enabled'], 'F019')}
              pendingValue={pendingChanges['gsensor_enable']}
              status={changeStatuses['gsensor_enable'] || 'normal'}
              error={errors['gsensor_enable']}
              onSave={(value) => handleSaveParameter('gsensor_enable', value)}
              disabled={!isEditMode}
            />
            
            {/* Sensitivity - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('gsensor_threshold')!}
              currentValue={getConfigValue('gsensor_threshold', ['g_sensor_threshold', 'motion_threshold'], 'F01A')}
              pendingValue={pendingChanges['gsensor_threshold']}
              status={changeStatuses['gsensor_threshold'] || 'normal'}
              error={errors['gsensor_threshold']}
              onSave={(value) => handleSaveParameter('gsensor_threshold', value)}
              disabled={!isEditMode}
            />
            
            {/* Events to Trigger - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('gsensor_count')!}
              currentValue={getConfigValue('gsensor_count', ['g_sensor_count'], 'F01C')}
              pendingValue={pendingChanges['gsensor_count']}
              status={changeStatuses['gsensor_count'] || 'normal'}
              error={errors['gsensor_count']}
              onSave={(value) => handleSaveParameter('gsensor_count', value)}
              disabled={!isEditMode}
            />
            
            {/* Time Window - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('gsensor_time_window')!}
              currentValue={getConfigValue('gsensor_time_window', ['g_sensor_time_gap', 'g_sensor_time'], 'F01D')}
              pendingValue={pendingChanges['gsensor_time_window']}
              status={changeStatuses['gsensor_time_window'] || 'normal'}
              error={errors['gsensor_time_window']}
              onSave={(value) => handleSaveParameter('gsensor_time_window', value)}
              disabled={!isEditMode}
            />
            {/* Trigger Mode Interval - EDITABLE (moved to motion section) */}
            <EditableConfigField
              parameter={getParameterConfig('trigger_mode_interval')!}
              currentValue={getConfigValue('trigger_mode_interval', ['sampling_interval', 'trigger_interval'], 'F02F')}
              pendingValue={pendingChanges['trigger_mode_interval']}
              status={changeStatuses['trigger_mode_interval'] || 'normal'}
              error={errors['trigger_mode_interval']}
              onSave={(value) => handleSaveParameter('trigger_mode_interval', value)}
              disabled={!isEditMode}
            />
            
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Measurement Range</span>
              <span className="font-medium">
                {(() => {
                  // F01B: G-sensor measurement range (00=±2G, 01=±4G, 02=±8G, 03=±16G)
                  const range = getConfigValue('gsensor_range', ['g_sensor_range'], 'F01B');
                  if (range === '00' || range === '0' || range === 0) return '±2G (Normal)';
                  if (range === '01' || range === '1' || range === 1) return '±4G (Sport)';
                  if (range === '02' || range === '2' || range === 2) return '±8G (Racing)';
                  if (range === '03' || range === '3' || range === 3) return '±16G (Extreme)';
                  return displayValue(range);
                })()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Environmental Monitoring - 6 parameters */}
      {(category === 'all' || category === 'environment') && (
        <div className={`border rounded-lg p-3 group ${
          Object.keys(changeStatuses).some(k => k.includes('temp_') || k.includes('humidity_') || k.includes('sensor_'))
            ? 'border-amber-300 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <Thermometer className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Environmental Monitoring</span>
          </div>
          <div className="space-y-1.5">
            {/* Sensor Monitoring Enable - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('sensor_alarm_enabled')!}
              currentValue={getConfigValue('sensor_alarm_enabled', ['temp_humi_alarm_enable'])}
              pendingValue={pendingChanges['sensor_alarm_enabled']}
              status={changeStatuses['sensor_alarm_enabled'] || 'normal'}
              error={errors['sensor_alarm_enabled']}
              onSave={(value) => handleSaveParameter('sensor_alarm_enabled', value)}
              disabled={!allowEditing || !isEditMode}
            />
            
            {/* Temperature Min - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('temp_threshold_low')!}
              currentValue={getConfigValue('temp_threshold_low', ['temperature_min', 'temp_min'])}
              pendingValue={pendingChanges['temp_threshold_low']}
              status={changeStatuses['temp_threshold_low'] || 'normal'}
              error={errors['temp_threshold_low']}
              onSave={(value) => handleSaveParameter('temp_threshold_low', value)}
              disabled={!allowEditing || !isEditMode}
            />
            
            {/* Temperature Max - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('temp_threshold_high')!}
              currentValue={getConfigValue('temp_threshold_high', ['temperature_max', 'temp_max'])}
              pendingValue={pendingChanges['temp_threshold_high']}
              status={changeStatuses['temp_threshold_high'] || 'normal'}
              error={errors['temp_threshold_high']}
              onSave={(value) => handleSaveParameter('temp_threshold_high', value)}
              disabled={!allowEditing || !isEditMode}
            />
            
            {/* Humidity Min - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('humidity_threshold_low')!}
              currentValue={getConfigValue('humidity_threshold_low', ['humidity_min', 'humi_min'])}
              pendingValue={pendingChanges['humidity_threshold_low']}
              status={changeStatuses['humidity_threshold_low'] || 'normal'}
              error={errors['humidity_threshold_low']}
              onSave={(value) => handleSaveParameter('humidity_threshold_low', value)}
              disabled={!allowEditing || !isEditMode}
            />
            
            {/* Humidity Max - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('humidity_threshold_high')!}
              currentValue={getConfigValue('humidity_threshold_high', ['humidity_max', 'humi_max'])}
              pendingValue={pendingChanges['humidity_threshold_high']}
              status={changeStatuses['humidity_threshold_high'] || 'normal'}
              error={errors['humidity_threshold_high']}
              onSave={(value) => handleSaveParameter('humidity_threshold_high', value)}
              disabled={!allowEditing || !isEditMode}
            />
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Check Interval</span>
              <span className="font-medium">
                {displayValue(getConfigValue('sensor_alarm_timer', ['temp_humi_time_gap']), 'seconds')}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Light Sensor Timer</span>
              <span className="font-medium">
                {(() => {
                  // F021: Light sensor timer (stored in milliseconds, display in minutes)
                  const ms = getConfigValue('light_sensor_timer', ['light_alarm_time_gap'], 'F021');
                  if (!ms) return '—';
                  const val = Number(ms);
                  // Convert milliseconds to minutes for display
                  const minutes = val / (1000 * 60);
                  return minutes >= 1 ? `${minutes} minutes` : `${val/1000} seconds`;
                })()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Safety Alerts - 3 parameters */}
      {(category === 'all' || category === 'safety') && (
        <div className={`border rounded-lg p-3 group ${
          Object.keys(changeStatuses).some(k => k.includes('speed') || k.includes('battery'))
            ? 'border-amber-300 bg-amber-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Safety Alerts</span>
          </div>
          <div className="space-y-1.5">
            {/* Speed Limit - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('max_speed_threshold')!}
              currentValue={getConfigValue('max_speed_threshold', ['speed_limit', 'speed_threshold_max'])}
              pendingValue={pendingChanges['max_speed_threshold']}
              status={changeStatuses['max_speed_threshold'] || 'normal'}
              error={errors['max_speed_threshold']}
              onSave={(value) => handleSaveParameter('max_speed_threshold', value)}
              disabled={!isEditMode}
            />
            
            {/* Low Battery Alert - EDITABLE */}
            <EditableConfigField
              parameter={getParameterConfig('low_battery_threshold')!}
              currentValue={getConfigValue('low_battery_threshold', ['battery_low_threshold'])}
              pendingValue={pendingChanges['low_battery_threshold']}
              status={changeStatuses['low_battery_threshold'] || 'normal'}
              error={errors['low_battery_threshold']}
              onSave={(value) => handleSaveParameter('low_battery_threshold', value)}
              disabled={!isEditMode}
            />
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">GPS Signal Loss</span>
              <span className="font-medium">
                {displayValue(getConfigValue('gps_blindness_timer'), 'seconds')}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Device Information - Enhanced with firmware and ICCID */}
      {(category === 'all' || category === 'device') && (
        <div className="border border-gray-200 rounded-lg bg-gray-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-900">Device Information</span>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Device ID (IMEI)</span>
              <span className="font-medium text-gray-900">
                {displayValue(getConfigValue('device_id_config', ['device_id']))}
              </span>
            </div>
            
            {/* Firmware Version */}
            {deviceInfo.firmware_version && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Firmware Version</span>
                <span className="font-medium text-gray-900 font-mono text-xs">
                  {deviceInfo.firmware_version}
                </span>
              </div>
            )}
            
            {/* ICCID (SIM Card ID) */}
            {deviceInfo.iccid && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">ICCID (SIM)</span>
                <span className="font-medium text-gray-900 font-mono text-xs">
                  {deviceInfo.iccid}
                </span>
              </div>
            )}
            
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">Last Update</span>
              <span className="font-medium">
                {(() => {
                  const lastSeen = deviceWithConfig.last_seen;
                  if (!lastSeen) return 'Never';
                  const date = new Date(lastSeen);
                  const now = new Date();
                  const diffMs = now.getTime() - date.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  if (diffMins < 60) return `${diffMins} min ago`;
                  const diffHours = Math.floor(diffMins / 60);
                  if (diffHours < 24) return `${diffHours} hours ago`;
                  return date.toLocaleDateString();
                })()}
              </span>
            </div>
            
            {/* Last Firmware Update */}
            {deviceWithConfig.last_firmware_update && (
              <div className="flex justify-between text-xs">
                <span className="text-gray-600">Firmware Updated</span>
                <span className="font-medium text-gray-500">
                  {(() => {
                    const date = new Date(deviceWithConfig.last_firmware_update);
                    const now = new Date();
                    const diffMs = now.getTime() - date.getTime();
                    const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
                    if (diffDays === 0) return 'Today';
                    if (diffDays === 1) return 'Yesterday';
                    if (diffDays < 30) return `${diffDays} days ago`;
                    return date.toLocaleDateString();
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceConfigDisplay;